import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  WalletIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'
import { walletApi, Transaction, FeeInvoice } from '@/api/walletApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const categoryColors: Record<string, string> = {
  SCHOOL_FEES: 'bg-blue-100 text-blue-800',
  TRANSPORT_FEES: 'bg-purple-100 text-purple-800',
  LIBRARY_FEES: 'bg-indigo-100 text-indigo-800',
  EXAM_FEES: 'bg-pink-100 text-pink-800',
  UNIFORM: 'bg-orange-100 text-orange-800',
  BOOKS: 'bg-teal-100 text-teal-800',
  MEALS: 'bg-yellow-100 text-yellow-800',
  ACTIVITIES: 'bg-green-100 text-green-800',
  TOP_UP: 'bg-emerald-100 text-emerald-800',
  REFUND: 'bg-red-100 text-red-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  PARTIALLY_PAID: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
}

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function StudentWalletDashboard() {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month')

  // Get wallet
  const { data: walletData, isLoading: walletLoading } = useQuery({
    queryKey: ['myWallet'],
    queryFn: () => walletApi.getMyWallet(),
  })

  // Get transaction summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['transactionSummary', period],
    queryFn: () => walletApi.getTransactionSummary(period),
  })

  // Get recent transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['myTransactions'],
    queryFn: () => walletApi.getTransactions({ limit: 10 }),
  })

  // Get invoices
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['myInvoices'],
    queryFn: () => walletApi.getInvoices({ limit: 5 }),
  })

  const isLoading = walletLoading || summaryLoading || transactionsLoading || invoicesLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const wallet = walletData?.data
  const summary = summaryData?.data?.summary
  const recentTransactions = transactionsData?.data?.transactions || []
  const invoices = invoicesData?.data?.invoices || []
  const pendingInvoices = invoices.filter(
    (inv: FeeInvoice) => inv.status !== 'PAID' && inv.status !== 'CANCELLED'
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Wallet</h1>
          <p className="text-gray-600">View your balance and transaction history</p>
        </div>
        <div className="flex gap-2">
          {(['week', 'month', 'year'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Balance Card */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-100 mb-1">My Balance</p>
            <p className="text-4xl font-bold">
              {formatCurrency(wallet?.balance || 0, wallet?.currency)}
            </p>
            <p className="mt-2 text-indigo-200 text-sm">
              Available for meals, library, and activities
            </p>
          </div>
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
            <WalletIcon className="w-10 h-10" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Spent ({period})</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary?.totalDebits || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <ArrowDownIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{summary?.debitCount || 0} transactions</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Received ({period})</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary?.totalCredits || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <ArrowUpIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{summary?.creditCount || 0} deposits</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fee Balance</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(
                  pendingInvoices.reduce((sum: number, inv: FeeInvoice) => sum + inv.balanceDue, 0)
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{pendingInvoices.length} pending invoices</p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <a
          href="/student/wallet/transactions"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-primary-500 hover:shadow-sm transition-all"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <ChartBarIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Transactions</p>
            <p className="text-sm text-gray-500">View history</p>
          </div>
        </a>

        <a
          href="/student/wallet/credits"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-green-500 hover:shadow-sm transition-all"
        >
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <ArrowUpIcon className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Credits</p>
            <p className="text-sm text-gray-500">Funds received</p>
          </div>
        </a>

        <a
          href="/student/wallet/debits"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-red-500 hover:shadow-sm transition-all"
        >
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <ArrowDownIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Debits</p>
            <p className="text-sm text-gray-500">My spending</p>
          </div>
        </a>

        <a
          href="/student/wallet/invoices"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-500 hover:shadow-sm transition-all"
        >
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <DocumentTextIcon className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Invoices</p>
            <p className="text-sm text-gray-500">Fee statements</p>
          </div>
        </a>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <a
              href="/student/wallet/transactions"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </a>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {recentTransactions.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No transactions yet</div>
          ) : (
            recentTransactions.slice(0, 5).map((transaction: Transaction) => (
              <div key={transaction._id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === 'CREDIT'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {transaction.type === 'CREDIT' ? (
                        <ArrowUpIcon className="w-5 h-5" />
                      ) : (
                        <ArrowDownIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          categoryColors[transaction.category] || categoryColors.OTHER
                        }`}
                      >
                        {transaction.category.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-semibold ${
                        transaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.type === 'CREDIT' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-xs text-gray-500">{formatDate(transaction.createdAt)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pending Invoices */}
      {pendingInvoices.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Pending Fee Invoices</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingInvoices.slice(0, 3).map((invoice: FeeInvoice) => (
              <div key={invoice._id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{invoice.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          categoryColors[invoice.category]
                        }`}
                      >
                        {invoice.category.replace('_', ' ')}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          statusColors[invoice.status]
                        }`}
                      >
                        {invoice.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(invoice.balanceDue)}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                      <ClockIcon className="w-3 h-3" />
                      Due: {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-gray-200">
            <a
              href="/student/wallet/invoices"
              className="block text-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all invoices
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
